/* eslint-disable no-unused-expressions */
/** @jsx jsx */
import React from "react";
import { graphql, navigate } from "gatsby";
import {
    jsx,
    Box,
    Button,
    Checkbox,
    Heading,
    Label,
    Radio,
    Input,
    Select,
    Textarea,
    Flex,
    Container,
    Grid
} from "theme-ui";
import { GatsbyImage } from "gatsby-plugin-image";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CustomLabel from "./CustomLabel";
import FormErrorMessage from "./FormErrorMessage";
import ContentfulRichText from "../ContentfulRichText";
import ModalForm from "./modalForm";
import EnhanceMarkup from "../EnhanceMarkup";

export const query = graphql`
  fragment FormFragment on ContentfulForm {
    formName
    formMaxWidth
    heading
    backgroundColor
    headingAs
    description {
      raw
    }
    type
    classname
    formHeading
    descriptionImage {
        gatsbyImageData
        file {
          contentType
          url
        }
    }
    redirectOnSubmit
    contentfulfields {
      fieldName
      label
      fieldType
      placeholderText
      required
      requiredValidationMessage
      options {
        label
        value
      }
    }
    alignment
  }
`;

const Form = ({
    formMaxWidth,
    formName,
    heading,
    description,
    descriptionImage,
    contentfulfields,
    onFinish,
    headingAs,
    backgroundColor,
    isModal,
    type,
    classname,
    formHeading,
    redirectOnSubmit,
    alignment,
}) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const encode = (data) => Object.keys(data)
        .map(
            (key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`
        )
        .join("&");

    const onSubmit = async (formData) => {
        try {
            const fetchResponse = await fetch("/?no-cache=1", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({ "form-name": formName, ...formData }),
            });

            if (fetchResponse.ok) {
                if(redirectOnSubmit) {
                    navigate(redirectOnSubmit);
                } else {
                    onFinish && onFinish();
                    toast.success("Your info has been submitted. Please check your email for next steps.");
                    reset();
                }
            }
        } catch (error) {
            toast.error(
                "An error occured! Please make sure you have internet connection and try again!"
            );
        }
    };
    if (isModal) {
        return <ModalForm
          formMaxWidth={formMaxWidth}
          formName={formName}
          heading={heading}
          description={description}
          contentfulfields={contentfulfields}
          onFinish={onFinish}
          headingAs={headingAs}
          backgroundColor={backgroundColor}
        />;
    }
    const textInputFields = contentfulfields?.filter((field) => field.fieldType === "text" || field.fieldType === "email") || [];
    if (!type) {
        return (
          <Flex
            mb={5}
            className={classname}
            sx={{
                padding: ["20px", "30px"],
                maxWidth: formMaxWidth,
                margin: "0 auto 50px auto",
                backgroundColor: `${backgroundColor}`,
                borderRadius: '20px',
                justifyContent: ['center', 'flex-start'],
                flexDirection: ['column', alignment === 'left' ? 'row-reverse' : 'row'],
                gap: "40px"
            }}
          >
              <Box className="form-description" sx={{ flex: '1 1 calc(50% - 20px)', width: ['100%', '100%'], maxWidth: '500px' }}>
                  {heading && (
                    <Heading as={headingAs || 'h5'} variant="extraBold"
                             sx={{color: backgroundColor === 'none' ? 'inherit' : '#FFF !important', paddingTop: '20px'}}>
                        <EnhanceMarkup text={heading}/>
                    </Heading>
                  )}
                  {descriptionImage && <Flex sx={{justifyContent: 'center', width: '100%', my: '20px'}}><GatsbyImage
                    image={descriptionImage.gatsbyImageData}
                    sx={{maxWidth: '80%', height: 'auto'}}
                  /></Flex>}
                  {description &&
                    <Box>
                        <ContentfulRichText richText={description} fontColor={backgroundColor === 'none' ? '#404041' : '#fff'}/>
                    </Box>
                  }
              </Box>
              <Box
                as="form"
                name={formName}
                id={formName}
                onSubmit={handleSubmit(onSubmit)}
                data-netlify="true"
                data-netlify-honeypot="bot"
                sx={{mt: "24px", flex: '1 1 calc(50% - 20px)'}}
              >
                  <input type="hidden" name="form-name" value={formName}/>
                  {formHeading && (
                    <Heading as="h5" variant="extraBold" sx={{
                        textAlign: 'center',
                        color: 'white !important',
                        paddingTop: '20px',
                        paddingBottom: '20px',
                        margin: 'auto'
                    }}>
                        {formHeading}
                    </Heading>
                  )}
                  {contentfulfields?.map((field) => {
                      const {
                          fieldName,
                          label,
                          fieldType,
                          placeholderText,
                          required,
                          requiredValidationMessage,
                          options,
                      } = field;

                      switch (fieldType) {
                          case "text":
                              return (
                                <Flex className="form-field" sx={{mb: "20px", flexDirection: 'column', alignItems: 'flex-start'}}
                                      key={label}>
                                    <Box className="label-field" sx={{minWidth: ['60px', '100px']}}>
                                        <CustomLabel colour={backgroundColor === 'none' ? '#404041' : "#FFFFFF"} required={required}
                                                     label={label}/>
                                    </Box>
                                    <Box sx={{width: '100%'}}>
                                        <Input
                                          {...register(fieldName, {
                                              required: required || requiredValidationMessage,
                                          })}
                                          id={fieldName}
                                          type="text"
                                          placeholder={placeholderText}
                                          sx={{
                                              borderColor: errors[fieldName] && "#E2226B",
                                              backgroundColor: errors[fieldName] ? '#FFEAF2' : '#fff',
                                              borderRadius: '30px',
                                              minWidth: '230px',
                                              outline: 'none',
                                              border: '1px solid #404041'
                                          }}
                                        />
                                        <FormErrorMessage error={errors[fieldName]}/>
                                    </Box>
                                </Flex>
                              );
                          case "email":
                              return (
                                <Flex className="form-field" sx={{mb: "20px", flexDirection: 'column', alignItems: 'flex-start'}}
                                      key={label}>
                                    <Box className="label-field" sx={{minWidth: ['60px', '100px']}}>
                                        <CustomLabel colour={backgroundColor === 'none' ? '#404041' : "#FFFFFF"} required={required}
                                                     label={label}/>
                                    </Box>
                                    <Box sx={{width: '100%'}}>
                                        <Input
                                          {...register(fieldName, {
                                              required: required || requiredValidationMessage,
                                              pattern: {
                                                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                  message: "Please enter a valid email to continue",
                                              },
                                          })}
                                          id={fieldName}
                                          type="email"
                                          placeholder={placeholderText}
                                          sx={{
                                              borderColor: errors[fieldName] && "#E2226B",
                                              backgroundColor: errors[fieldName] ? '#FFEAF2' : '#fff',
                                              borderRadius: '30px',
                                              minWidth: '250px',
                                              outline: 'none',
                                              border: '1px solid #404041'
                                          }}
                                        />
                                        <FormErrorMessage error={errors[fieldName]}/>
                                    </Box>
                                </Flex>
                              );
                          case "radio":
                              return (
                                <Flex className="form-field radio-button" key={label} sx={{
                                    justifyContent: 'center',
                                    alignItems: 'flexStart',
                                    marginBottom: '20px',
                                    marginTop: "24px",
                                    flexDirection: 'column'
                                }}>
                                    <Box className="label-field">
                                        <CustomLabel colour={backgroundColor === 'none' ? '#404041' : "#FFFFFF"} required={required}
                                                     label={label}/>
                                    </Box>
                                    <Box>
                                        {options.map((option) => (
                                          <Label key={option.label}>
                                              <Radio
                                                {...register(fieldName, {
                                                    required: required || requiredValidationMessage,
                                                })}
                                                value={option.value}
                                              />
                                              {option.label}
                                          </Label>
                                        ))}
                                        <FormErrorMessage error={errors[fieldName]}/>
                                    </Box>
                                </Flex>
                              );
                          case "dropdown":
                              return (
                                <Flex className="form-field" key={label}
                                      sx={{mb: "20px", justifyContent: 'center', alignItems: 'baseline', flexDirection: "column"}}>
                                    <Box className="label-field">
                                        <CustomLabel colour={backgroundColor === 'none' ? '#404041' : "#FFFFFF"} required={required} label={label}/>
                                    </Box>
                                    <Box sx={{ width: "100%" }}>
                                        <Select
                                          {...register(fieldName, {
                                              required: required || requiredValidationMessage,
                                          })}
                                          id={fieldName}
                                          sx={{
                                              borderColor: errors[fieldName] && "#E2226B",
                                              backgroundColor: errors[fieldName] ? '#FFEAF2' : '#fff',
                                              borderRadius: '30px',
                                              minWidth: '250px',
                                              outline: 'none',
                                              border: '1px solid #404041',
                                              minHeight: '45px',
                                          }}
                                        >
                                            <option key={fieldName} selected value="">
                                                Please select an option
                                            </option>
                                            {options.map((option) => (
                                              <option key={option.label} value={option.value}>
                                                  {option.label}
                                              </option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage error={errors[fieldName]}/>
                                    </Box>
                                </Flex>
                              );
                          case "textarea":
                              return (
                                <Flex className="form-field" key={label} sx={{justifyContent: 'center', alignItems: 'baseline'}}>
                                    <Box className="label-field">
                                        <CustomLabel colour={backgroundColor === 'none' ? '#404041' : "#FFFFFF"} required={required}
                                                     label={label}/>
                                    </Box>
                                    <Box>
                                        <Textarea
                                          {...register(fieldName, {
                                              required: required || requiredValidationMessage,
                                          })}
                                          id={fieldName}
                                          rows={6}
                                          placeholder={placeholderText}
                                          sx={{
                                              borderColor: errors[fieldName] && "#E2226B",
                                              backgroundColor: errors[fieldName] ? '#FFEAF2' : '#fff',
                                              borderRadius: '30px',
                                              minWidth: '250px',
                                              outline: 'none',
                                              border: '1px solid #404041'
                                          }}
                                        />
                                        <FormErrorMessage error={errors[fieldName]}/>
                                    </Box>
                                </Flex>
                              );
                          case "checkbox":
                              return (
                                <Flex className="form-field checkbox" key={label} sx={{
                                    justifyContent: 'center',
                                    alignItems: 'baseline',
                                    flexDirection: "column",
                                    marginTop: '24px'
                                }}>
                                    <Box className="label-field">
                                        <CustomLabel colour="#1A2238" required={required} label={label}/>
                                    </Box>
                                    <Box>
                                        {options.map((option) => {
                                            if (option.value === "other") {
                                                return (
                                                  <Box>
                                                      <Label key={option.label}>
                                                          <Checkbox
                                                            {...register(fieldName, {
                                                                required:
                                                                  required || requiredValidationMessage,
                                                            })}
                                                            value={option.value}
                                                          />
                                                          {option.label}
                                                      </Label>
                                                      <Input
                                                        {...register(`${fieldName}-other`)}
                                                        sx={{maxWidth: "300px"}}
                                                      />
                                                  </Box>
                                                );
                                            }
                                            return (
                                              <Label key={option.label}>
                                                  <Checkbox
                                                    {...register(fieldName, {
                                                        required: required || requiredValidationMessage,
                                                    })}
                                                    value={option.value}
                                                  />
                                                  {option.label}
                                              </Label>
                                            );
                                        })}
                                        <FormErrorMessage error={errors[fieldName]}/>
                                    </Box>
                                </Flex>
                              );
                          case "submit":
                              return (
                                <Flex className="form-submit" key={label}
                                      sx={{justifyContent: 'center', alignItems: 'baseline', flexDirection: 'column'}}>
                                    <Box className="label-submit form-submit-empty" sx={{minWidth: ['60px', '100px']}}>
                                        <CustomLabel/>
                                    </Box>
                                    <Box>
                                        <Button type="submit" variant="buttons.primaryV2"
                                                sx={{minWidth: ['250px', '200px', null, '250px'], width: '100%', minHeight: '45px'}}
                                                id="submit">
                                            {label}
                                        </Button>
                                    </Box>
                                </Flex>
                              );
                          default:
                              return null;
                      }
                  })}
              </Box>
          </Flex>);
    }
    return (
      <Container mb={5} sx={{
          px: ["0px", "0px", "0px", "0", "0"],
          margin: "0 auto 50px auto",
          backgroundColor: `${backgroundColor}`,
          borderRadius: '20px',
          justifyContent: 'center',
          alignItems: 'center'
      }}>
          <Box sx={{width: '100%', pt: '50px', pb: "10px", backgroundColor: "midnightblue", textAlign: "center"}}>
              {heading && (
                <Heading as={headingAs || 'h5'} variant="extraBold"
                         sx={{color: 'white', lineHeight: "36px", fontSize: "24px", fontWeight: "600"}}>
                    {heading}
                </Heading>
              )}
          </Box>
          <Box sx={{px: ["16px", "18px", "40px"], textAlign: "justify"}}>
              {description &&
                <Box>
                    <ContentfulRichText richText={description} fontColor={backgroundColor && '#fff'}/>
                </Box>
              }
          </Box>
          <Box
            as="form"
            name={formName}
            id={formName}
            onSubmit={handleSubmit(onSubmit)}
            data-netlify="true"
            data-netlify-honeypot="bot"
            sx={{mt: "24px"}}
          >
              <input type="hidden" name="form-name" value={formName}/>
              <Grid gap="0px 40px" columns={[1, 1, 2, 2]} sx={{px: ["16px", "18px", "40px"]}}>
                  {textInputFields?.map((field) => {
                      const {
                          fieldName,
                          label,
                          placeholderText,
                          required,
                          requiredValidationMessage
                      } = field;
                      return (<Flex sx={{
                          flexDirection: ['column', 'column', 'row'],
                          mb: "10px",
                          justifyContent: ['left', 'left', 'space-between', 'space-between'],
                          alignItems: 'center'
                      }} key={label}>
                          <Box>
                              <CustomLabel colour="#00000" required={required} label={label}/>
                          </Box>
                          <Box>
                              <Input
                                {...register(fieldName, {
                                    required: required || requiredValidationMessage,
                                })}
                                id={fieldName}
                                type="text"
                                placeholder={placeholderText}
                                sx={{
                                    borderColor: errors[fieldName] && "#E2226B",
                                    backgroundColor: errors[fieldName] ? '#FFEAF2' : '#fff',
                                    borderRadius: '30px',
                                    minWidth: '250px',
                                    outline: 'none',
                                    border: '1px solid #404041'
                                }}
                              />
                              <FormErrorMessage error={errors[fieldName]}/>
                          </Box>
                      </Flex>);
                  })}
              </Grid>
              {contentfulfields?.map((field) => {
                  const {
                      fieldName,
                      label,
                      fieldType,
                      placeholderText,
                      required,
                      requiredValidationMessage,
                      options,
                  } = field;
                  switch (fieldType) {
                      case "radio":
                          return (
                            <Container sx={{px: ["16px", "18px", "40px"]}} key={label}>
                                <Box sx={{mb: '15px'}}>
                                    <CustomLabel colour="#1A2238" required={required} label={label}/>
                                </Box>
                                <Box>
                                    {options.map((option) => (
                                      <Label key={option.label}>
                                          <Radio
                                            sx={{'input:checked ~ &': {color: "#272763"}, color: "#1A2238"}}
                                            {...register(fieldName, {
                                                required: required || requiredValidationMessage,
                                            })}
                                            value={option.value}
                                          />
                                          {option.label}
                                      </Label>
                                    ))}
                                    <FormErrorMessage error={errors[fieldName]}/>
                                </Box>
                            </Container>
                          );
                      case "dropdown":
                          return (
                            <Flex key={label} sx={{mb: "20px", justifyContent: 'center', alignItems: 'baseline'}}>
                                <Box>
                                    <CustomLabel colour="#1A2238" required={required} label={label}/>
                                </Box>
                                <Box>
                                    <Select
                                      {...register(fieldName, {
                                          required: required || requiredValidationMessage,
                                      })}
                                      id={fieldName}
                                      sx={{
                                          borderColor: errors[fieldName] && "#E2226B",
                                          backgroundColor: errors[fieldName] ? '#FFEAF2' : '#fff',
                                          borderRadius: '30px',
                                          minWidth: '250px',
                                          outline: 'none',
                                          border: '1px solid #404041',
                                          minHeight: '45px',
                                      }}
                                    >
                                        <option key={fieldName} selected value="">
                                            Please select an option
                                        </option>
                                        {options.map((option) => (
                                          <option key={option.label} value={option.value}>
                                              {option.label}
                                          </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage error={errors[fieldName]}/>
                                </Box>
                            </Flex>
                          );
                      case "textarea":
                          return (
                            <Flex key={label} sx={{justifyContent: 'center', alignItems: 'baseline'}}>
                                <Box>
                                    <CustomLabel colour="#1A2238" required={required} label={label}/>
                                </Box>
                                <Box>
                                    <Textarea
                                      {...register(fieldName, {
                                          required: required || requiredValidationMessage,
                                      })}
                                      id={fieldName}
                                      rows={6}
                                      placeholder={placeholderText}
                                      sx={{
                                          borderColor: errors[fieldName] && "#E2226B",
                                          backgroundColor: errors[fieldName] ? '#FFEAF2' : '#fff',
                                          borderRadius: '30px',
                                          minWidth: '250px',
                                          outline: 'none',
                                          border: '1px solid #404041'
                                      }}
                                    />
                                    <FormErrorMessage error={errors[fieldName]}/>
                                </Box>
                            </Flex>
                          );
                      case "checkbox":
                          return (
                            <Container sx={{px: ["16px", "18px", "40px"]}} key={label}>
                                <Box sx={{mb: '15px'}}>
                                    <CustomLabel colour="#1A2238" required={required} label={label}/>
                                </Box>
                                <Grid gap="0px 40px" columns={[2, 2, 2, 2]}>
                                    {options.map((option) => {
                                        if (option.value === "other") {
                                            return (
                                              <Box>
                                                  <Label key={option.label}>
                                                      <Checkbox
                                                        sx={{'input:checked ~ &': {color: "#272763"}, color: "#1A2238"}}
                                                        {...register(fieldName, {
                                                            required:
                                                              required || requiredValidationMessage,
                                                        })}
                                                        value={option.value}
                                                      />
                                                      {option.label}
                                                  </Label>
                                                  <Input
                                                    {...register(`${fieldName}-other`)}
                                                    sx={{maxWidth: "300px"}}
                                                  />
                                              </Box>
                                            );
                                        }
                                        return (
                                          <Label key={option.label}>
                                              <Checkbox
                                                sx={{'input:checked ~ &': {color: "#272763"}, color: "#1A2238"}}
                                                {...register(fieldName, {
                                                    required: required || requiredValidationMessage,
                                                })}
                                                value={option.value}
                                              />
                                              {option.label}
                                          </Label>
                                        );
                                    })}
                                    <FormErrorMessage error={errors[fieldName]}/>
                                </Grid>
                            </Container>
                          );
                      case "text-additional":
                          return (
                            <Container sx={{px: ["16px", "18px", "40px"]}} key={label}>
                                <Flex sx={{
                                    flexDirection: ['column', 'column', 'row'],
                                    mb: "10px",
                                    justifyContent: ['left', 'left', 'left', 'left'],
                                    alignItems: 'center'
                                }} key={label}>
                                    <Box>
                                        <CustomLabel colour="#00000" required={required} label={label}/>
                                    </Box>
                                    <Box>
                                        <Input
                                          {...register(fieldName, {
                                              required: required || requiredValidationMessage,
                                          })}
                                          id={fieldName}
                                          type="text"
                                          placeholder={placeholderText}
                                          sx={{
                                              borderColor: errors[fieldName] && "#E2226B",
                                              backgroundColor: errors[fieldName] ? '#FFEAF2' : '#fff',
                                              borderRadius: '30px',
                                              minWidth: '250px',
                                              outline: 'none',
                                              border: '1px solid #404041',
                                              ml: "10px"
                                          }}
                                        />
                                        <FormErrorMessage error={errors[fieldName]}/>
                                    </Box>
                                </Flex>
                            </Container>
                          );
                      case "submit":
                          return (
                            <Container sx={{px: ["16px", "18px", "40px"], mt: "20px"}} key={label}>
                                <Box>
                                    <CustomLabel/>
                                </Box>
                                <Box>
                                    <Button type="submit" variant="buttons.primaryV2" sx={{width: '100%', py: "15px", height: "auto"}}>
                                        {label}
                                    </Button>
                                </Box>
                            </Container>
                          );
                      default:
                          return null;
                  }
              })}
          </Box>
      </Container>);

};

export default Form;